import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'

// Layout components
import { Layout } from './components/Layout'
import { LoadingSpinner } from './components/LoadingSpinner'

// Public pages
import { HomePage } from './pages/HomePage'
import { PackagesPage } from './pages/PackagesPage'
import { PackageDetailPage } from './pages/PackageDetailPage'
import { PackageBuilderPage } from './pages/PackageBuilderPage'
import { QuoteDetailPage } from './pages/QuoteDetailPage'
import { ToolkitsPage } from './pages/ToolkitsPage'
import { AboutPage } from './pages/AboutPage'
import { ContactPage } from './pages/ContactPage'
import ShopPage from './pages/ShopPage'
import CheckoutSuccessPage from './pages/CheckoutSuccessPage'

// Lazy-load Stripe checkout (loadStripe runs at module scope)
const SimpleCheckoutPage = lazy(() => import('./pages/SimpleCheckoutPage'))

// Auth pages
import { DashboardPage } from './pages/DashboardPage'
import { AdminPage } from './pages/AdminPage'

// Other placeholder pages
import {
  CheckoutPage,
  OrdersPage,
  ServicesPage,
  BillingPage,
  ProfilePage,
  LoginPage,
  LogoutPage,
  NotFoundPage
} from './pages/PlaceholderPages'

function App() {
  const { isLoading, isAuthenticated } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/packages" element={<PackagesPage />} />
        <Route path="/packages/:slug" element={<PackageDetailPage />} />
        <Route path="/packages/:slug/customize" element={<PackageBuilderPage />} />
        <Route path="/quotes/:quoteId" element={<QuoteDetailPage />} />
        <Route path="/toolkits" element={<ToolkitsPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/checkout/:productId" element={<Suspense fallback={<LoadingSpinner size="lg" />}><SimpleCheckoutPage /></Suspense>} />
        <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logout" element={<LogoutPage />} />

        {/* Protected Routes */}
        {isAuthenticated && (
          <>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </>
        )}

        {/* Catch all - 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  )
}

export default App